import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext } from 'react';
import { useCallback, useContext, useMemo } from 'react';
import classnames from 'classnames';
import { useUncontrolled } from 'uncontrollable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Collapse } from '../Transitions/Collapse';
export const PanelContext = /*#__PURE__*/ createContext({});
const PanelBody = (props)=>{
    const { innerRef, collapsible, ...rest } = props;
    const body = /*#__PURE__*/ _jsx("div", {
        ...rest,
        ref: innerRef,
        className: classnames('card-body bg-white text-dark rounded-bottom', props.className)
    });
    if (collapsible) {
        return /*#__PURE__*/ _jsx(PanelCollapse, {
            children: body
        });
    }
    return body;
};
const PanelHeader = (props)=>{
    return /*#__PURE__*/ _jsx("div", {
        ...props,
        className: classnames('card-header', props.className)
    });
};
const PanelToggle = (props)=>{
    const { onToggle, expanded } = useContext(PanelContext);
    const handleToggle = useCallback((event)=>{
        if (onToggle) {
            onToggle(event);
        }
    }, [
        onToggle
    ]);
    const { children } = props;
    return /*#__PURE__*/ _jsxs("a", {
        "aria-expanded": expanded,
        onClick: handleToggle,
        role: "button",
        className: "d-flex align-items-center",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex-grow-1",
                children: children
            }),
            /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                icon: expanded ? faChevronDown : faChevronRight,
                fixedWidth: true,
                className: "ms-3"
            })
        ]
    });
};
const PanelTitle = (props)=>{
    const { toggle, ...rest } = props;
    const title = /*#__PURE__*/ _jsx("div", {
        ...rest,
        className: classnames('card-title', props.className)
    });
    if (toggle) {
        return /*#__PURE__*/ _jsx(PanelToggle, {
            children: title
        });
    }
    return title;
};
const PanelFooter = (props)=>{
    return /*#__PURE__*/ _jsx("div", {
        ...props,
        className: classnames('card-footer', props.className)
    });
};
const PanelCollapse = (props)=>{
    const { children, ...rest } = props;
    const { expanded } = useContext(PanelContext);
    return /*#__PURE__*/ _jsx(Collapse, {
        in: expanded,
        ...rest,
        children: /*#__PURE__*/ _jsx("div", {
            children: children
        })
    });
};
export const Panel = (props)=>{
    const { bsStyle, expanded, // eslint-disable-next-line @typescript-eslint/no-unused-vars
    defaultExpanded = true, onToggle, className, ...rest } = useUncontrolled(props, {
        expanded: 'onToggle'
    });
    const handleToggle = useCallback(()=>{
        onToggle && onToggle(!expanded);
    }, [
        onToggle,
        expanded
    ]);
    const context = useMemo(()=>({
            expanded,
            onToggle: handleToggle
        }), [
        expanded,
        onToggle
    ]);
    return /*#__PURE__*/ _jsx(PanelContext.Provider, {
        value: context,
        children: /*#__PURE__*/ _jsx("div", {
            className: classnames('card', 'panel', className, bsStyle ? `bg-${bsStyle} rcc-text-contrast-${bsStyle}` : ''),
            style: {
                marginBottom: '1.5rem'
            },
            ...rest
        })
    });
};
Panel.Body = PanelBody;
Panel.Collapse = PanelCollapse;
Panel.Heading = PanelHeader;
Panel.Title = PanelTitle;
Panel.Toggle = PanelToggle;
Panel.Footer = PanelFooter;
